import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localePl from '@angular/common/locales/pl';
import { ApplicationConfig, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  ArdCalendarView,
  BadgeSize,
  ButtonAppearance,
  ComponentColor,
  DecorationElementAppearance,
  DropdownPanelAppearance,
  FormElementAppearance,
  FormElementVariant,
  OneAxisAlignment,
  provideBadgeDefaults,
  provideBreakpoints,
  provideButtonDefaults,
  provideDateInputDefaults,
  provideDeletableChipDefaults,
  provideDialogDefaults,
  provideErrorMap,
  provideFormFieldDefaults,
  provideIconButtonDefaults,
  provideInputDefaults,
  provideNumberInputDefaults,
  provideRatingDisplayDefaults,
  provideSelectDefaults,
  provideSliderDefaults,
  provideSpinnerDefaults,
  provideTabberDefaults,
} from '@ardium-ui/ui';
import {
  convertStringToDate,
  isIsoDateString,
  provideMappingInterceptor,
} from '@common/interceptors/date-mapping.interceptor';
import { ERROR_MAP } from '@common/pipes/map-error.pipe';
import { RobotsService } from '@common/services/robots.service';
import { DATE_DESERIALIZATION_FN, DATE_SERIALIZATION_FN } from '@common/utils/date-serialization';
import { NgxEditorConfig } from './admin-panel/components/rich-text-editor/rich-text-editor.config';
import { routes } from './app.routes';

registerLocaleData(localePl);

export const appConfig: ApplicationConfig = {
  providers: [
    RobotsService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'pl-PL' },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: '+0000', dateFormat: 'dd.MM.yyyy' } },
    provideMappingInterceptor(isIsoDateString, convertStringToDate),
    importProvidersFrom(NgxEditorConfig),
    provideButtonDefaults({ appearance: ButtonAppearance.Flat, color: ComponentColor.Primary }),
    provideIconButtonDefaults({ color: ComponentColor.None }),
    provideDialogDefaults({
      rejectButtonAppearance: ButtonAppearance.Transparent,
      rejectButtonText: 'Anuluj',
      confirmButtonAppearance: ButtonAppearance.Flat,
    }),
    provideInputDefaults({
      placeholder: '',
      appearance: FormElementAppearance.Filled,
      autoTrim: true,
      inputAttrs: { autocomplete: 'one-time-code' },
    }),
    provideFormFieldDefaults({
      labelOptionalText: '(opcjonalne)',
    }),
    provideTabberDefaults({
      tabPointerEventsWhenDisabled: true,
    }),
    provideSelectDefaults({
      placeholder: '',
      appearance: FormElementAppearance.Filled,
      clearable: false,
      searchable: true,
      hideSelected: false,
      multiselectable: false,
      dropdownAppearance: DropdownPanelAppearance.Outlined,
      clearButtonTitle: 'Wyczyść',
      loadingPlaceholderText: 'Ładowanie...',
      noItemsFoundText: 'Nie znaleziono',
      addCustomOptionText: 'Utwórz',
    }),
    provideNumberInputDefaults({
      placeholder: '',
      appearance: FormElementAppearance.Filled,
      alignText: OneAxisAlignment.Left,
      noButtons: true,
      min: 0,
      allowFloat: false,
      decimalSeparator: ',',
      fixedDecimalPlaces: false,
      maxDecimalPlaces: 2,
    }),
    provideDateInputDefaults({
      placeholder: '',
      UTC: true,
      serializeFn: DATE_SERIALIZATION_FN,
      deserializeFn: DATE_DESERIALIZATION_FN,
      min: new Date(1901, 0, 1),
      max: new Date(2050, 11, 31),
      startView: ArdCalendarView.Years,
      appearance: FormElementAppearance.Filled,
    }),
    provideBadgeDefaults({
      color: ComponentColor.Primary,
      size: BadgeSize.Small,
      overlap: true,
      variant: FormElementVariant.Pill,
    }),
    provideSpinnerDefaults({ color: ComponentColor.None }),
    provideRatingDisplayDefaults({ max: 5, color: ComponentColor.Primary }),
    provideDeletableChipDefaults({
      appearance: DecorationElementAppearance.Filled,
      color: ComponentColor.None,
      contentAlignment: OneAxisAlignment.Right,
      deleteButtonTitle: 'Usuń',
    }),
    provideSliderDefaults({
      emitTouchedOnEveryChange: true,
    }),
    provideErrorMap(ERROR_MAP),
    provideBreakpoints(),
  ],
};
